/**
 * Lookup Type Hierarchy Modal Component
 * Shows the tree structure of lookup type relationships
 * Dynamically reads hierarchy from uiSchema
 */

import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  useTheme,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { UISchemaResponse, LookupType } from '../types/configRegistryTypes';

interface LookupTypeHierarchyModalProps {
  open: boolean;
  onClose: () => void;
  uiSchema: UISchemaResponse | null;
}

interface HierarchyRelation {
  parent: LookupType;
  children: LookupType[];
}

export const LookupTypeHierarchyModal: React.FC<LookupTypeHierarchyModalProps> = ({
  open,
  onClose,
  uiSchema,
}) => {
  const theme = useTheme();

  // Dynamically build hierarchy from uiSchema
  const { hierarchyRelations, independentTypes } = useMemo(() => {
    if (!uiSchema?.lookupTypes) {
      return { hierarchyRelations: [], independentTypes: [] };
    }

    const lookupTypes = uiSchema.lookupTypes;

    // Find all parent types (those that have children pointing to them)
    const parentCodes = new Set<string>();
    const childToParent = new Map<string, string>();

    lookupTypes.forEach(lt => {
      const parentCode = lt.parentTypeCode || lt.dependsOn;
      if (parentCode) {
        parentCodes.add(parentCode);
        childToParent.set(lt.code, parentCode);
      }
    });

    // Build hierarchy relations
    const relations: HierarchyRelation[] = [];
    parentCodes.forEach(parentCode => {
      const parentType = lookupTypes.find(lt => lt.code === parentCode);
      if (parentType) {
        const children = lookupTypes.filter(lt =>
          (lt.parentTypeCode === parentCode || lt.dependsOn === parentCode)
        );
        if (children.length > 0) {
          relations.push({ parent: parentType, children });
        }
      }
    });

    // Find independent types (no parent and not a parent themselves that has children)
    const independent = lookupTypes.filter(lt => {
      const hasParent = lt.parentTypeCode || lt.dependsOn;
      return !hasParent;
    });

    return { hierarchyRelations: relations, independentTypes: independent };
  }, [uiSchema?.lookupTypes]);

  const getFieldTypeBadge = (lt: LookupType | undefined) => {
    if (!lt) return null;
    const isMulti = lt.allowMultiSelect || (lt as any).fieldType === 'multi-select';
    return (
      <Chip
        label={isMulti ? 'Multi' : 'Single'}
        size="small"
        color={isMulti ? 'primary' : 'default'}
        sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountTreeIcon color="primary" />
        <Typography variant="h6">Lookup Type Hierarchy</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This shows how lookup types are related. Child lookup types only appear after their parent is selected.
        </Typography>

        {/* Hierarchical Relationships */}
        {hierarchyRelations.length > 0 && (
          <>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Parent → Child Relationships
            </Typography>
            <Box sx={{ mb: 3 }}>
              {hierarchyRelations.map((rel) => (
                <Box
                  key={rel.parent.code}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    bgcolor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography fontWeight={600}>{rel.parent.displayName || rel.parent.code}</Typography>
                    {getFieldTypeBadge(rel.parent)}
                  </Box>
                  {rel.children.map((child) => (
                    <Box
                      key={child.code}
                      sx={{ display: 'flex', alignItems: 'center', ml: 2, mt: 0.5, flexWrap: 'wrap' }}
                    >
                      <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                      <Typography variant="body2">{child.displayName || child.code}</Typography>
                      {getFieldTypeBadge(child)}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (appears when {rel.parent.displayName || rel.parent.code} is selected)
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Independent Types */}
        {independentTypes.length > 0 && (
          <>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Independent Lookup Types
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              These lookup types are always visible and don&apos;t depend on any parent selection.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {independentTypes.map((lt) => {
                const isMulti = lt.allowMultiSelect || (lt as any)?.fieldType === 'multi-select';
                return (
                  <Chip
                    key={lt.code}
                    icon={<RadioButtonUncheckedIcon />}
                    label={lt.displayName || lt.code}
                    variant="outlined"
                    color={isMulti ? 'primary' : 'default'}
                    size="small"
                  />
                );
              })}
            </Box>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Admin Request Note */}
        <Box sx={{
          p: 1.5,
          bgcolor: 'info.main',
          borderRadius: 1,
          opacity: 0.9,
        }}>
          <Typography variant="body2" sx={{ color: 'info.contrastText' }}>
            <strong>Note:</strong> If you need a new lookup type relationship, please contact the CMS Admin.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

